import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { Button, Form, Input, notification } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GQL_LOGIN } from '~/gql/auth/auth';
import { Payload, useAuth } from '~/helpers/store/auth';
import useTranslations from '~/helpers/store/locale/translation';
import { Role, routingDefault } from '~/pages/utils/router';
import { classic } from '~/themes';
import { parseJwt } from '~/utils/jwt';
import { SH2, SLink } from '~/utils/ui/utils';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { setPayload } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useLingui();

  const { changeLanguage } = useTranslations();

  const handleLanguageChange = (newLocale: string) => {
    changeLanguage(newLocale);
  };

  const [login, { loading }] = useLazyQuery(GQL_LOGIN, {
    onCompleted: (data) => {
      if (data.login.__typename === 'Tokens') {
        const dataJwt = parseJwt(data.login.access_token);
        if (!dataJwt) {
          notification.error({
            message: i18n._('common.login'),
            description: i18n._('impossible.read.token'),
            placement: 'topRight',
          });
          return;
        }
        const payload: Payload = {
          access_token: data.login.access_token,
          refresh_token: data.login.refresh_token,
          ...dataJwt,
        };
        notification.success({
          message: i18n._('common.login'),
          description: i18n._('connection.successful'),
          placement: 'topRight',
        });

        setPayload(payload);

        routingDefault(payload.roles as Role, navigate);
      } else {
        notification.error({
          message: i18n._('common.login'),
          description: i18n._('connection.failed'),
          placement: 'topRight',
        });
      }
    },
    onError: () => {
      notification.error({
        message: i18n._('common.login'),
        description: i18n._('bad.server-login.message'),
        placement: 'topRight',
      });
    },
  });

  const loginHandler = async () => {
    await login({ variables: { email: email, password: password } });
  };

  if (loading) return <p>{i18n._('common.loading')} ...</p>;

  return (
    <LoginForm>
      <SForm onFinish={loginHandler} initialValues={{ remember: true }}>
        <Form.Item>
          <SH2 $color={classic.token?.colorPrimary}>
            {i18n._('common.login')}
          </SH2>
        </Form.Item>

        <Form.Item
          name="email"
          className="customInput"
          rules={[
            {
              required: true,
              message: i18n._('input.email.message'),
              pattern: new RegExp(/^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/g),
            },
          ]}
        >
          <Input
            prefix={<UserOutlined className="site-form-item-icon" />}
            placeholder={i18n._('common.email')}
            value={'raphaelmortier@oulook.fr'}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Form.Item>
        <Form.Item
          name="password"
          className="customInput"
          rules={[{ required: true, message: i18n._('input.password') }]}
        >
          <Input
            prefix={<LockOutlined className="site-form-item-icon" />}
            type="password"
            placeholder={i18n._('common.password')}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="login-form-button"
          >
            {i18n._('common.login')}
          </Button>
        </Form.Item>

        <SLink
          $color="#9291a5"
          $textdecoration="underline"
          onClick={() => navigate('/reset-password')}
          $margin="0"
        >
          {i18n._('password.forgot')}
        </SLink>

        <LanguageSelectDiv>
          <SButton onClick={() => handleLanguageChange('en')}>
            {i18n._('common.english')}
          </SButton>
          <SButton onClick={() => handleLanguageChange('fr')}>
            {i18n._('common.french')}
          </SButton>
        </LanguageSelectDiv>
      </SForm>
    </LoginForm>
  );
}

export default Login;

export const LoginForm = styled.div<{ $width?: string }>`
  justify-content: center;
  align-items: center;
  width: ${({ $width }) => ($width ? $width : '100%')};

  @media (max-width: 992px) {
    height: auto;
    width: 100%;
  }
`;

export const SForm = styled(Form)`
  min-width: 25vw;
  background: white;
  height: 100%;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 15px;

  & .customInput {
    width: 80%;

    & span:nth-child(1),
    & input {
      background-color: rgba(240, 237, 255, 0.8);
    }
  }

  @media (max-width: 992px) {
    & .customInput {
      width: 100%;
    }
  }
`;

const LanguageSelectDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  & button {
    margin: 5% 2%;
  }
`;

export const SButton = styled.button`
  border-radius: 8px;
  border: 1px solid transparent;
  padding: 0.6em 1.2em;
  font-size: 1em;
  font-weight: 500;
  font-family: inherit;
  background-color: #1a1a1a;
  cursor: pointer;
  transition: border-color 0.25s;
  &:hover {
    border-color: #646cff;
  }
  &:focus,
  &:focus-visible {
    outline: 4px auto -webkit-focus-ring-color;
  }
`;
